import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductClearanceStyles {
    clearance?: InjectableCss;
    clearanceTooltip?: InjectableCss;
    messageText?: TypographyPresentationProps;
    customPropertyContainer?: InjectableCss;
}

export const ProductClearanceStyles: ProductClearanceStyles = {
    clearance:{
        css: css`
            background: url(/UserFiles/Images/Icons/clearance-pdp.png) no-repeat 0 0;
            padding: 11px 0 7px 36px;
            color: #ef3742;
            font-size: 13px;
            font-weight: bold;
            margin-top: 15px;
            position: relative;
            background-position: realtive;
            &:hover {
                > div {
                    visibility: visible;
                }
            }
        `,
    },
    clearanceTooltip: {
        css: css`
            visibility: hidden;
            font-size: 12px;
            background: #353432;
            color: #fff;
            font-weight: normal;
            border-radius: 6px;
            position: absolute;
            z-index: 1;
            top: 110%;
            left: 0;
            padding: 5px 7px;
            width:140px;
            &:before {
                content: "";
                display: block;
                width: 0;
                height: 0;
                position: absolute;
                border-left: 8px solid transparent;
                border-right: 8px solid transparent;
                border-bottom: 8px solid #353432;
                left: 30%;
                top: -8px;
                z-index: 99;
            }
        `,
    },
    customPropertyContainer: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const productState = getProductStateByPath(state, productPath);
    return {
        parentProductState: productState,
    };
};

type props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = ProductClearanceStyles;

const ProductClearance: React.FC<props> = props => {
    const showDiscontinuedTag = props.parentProductState?.value && props.parentProductState?.value.properties?.discontinuedDate ? 
        new Date(props.parentProductState.value.properties.discontinuedDate) < new Date(): 
        false;
    if(showDiscontinuedTag) {
        return null;
    }

    return (
        <StyledWrapper {...styles.customPropertyContainer}>
            {props.parentProductState.value?.properties["clearance"]?.toLowerCase() === "true" ? (
                <StyledWrapper {...styles.clearance} data-test-selector="shippingOversize_wrapper">
                    {translate("Clearance Item")}
                    <StyledWrapper {...styles.clearanceTooltip}>
                    {siteMessage("Product_Clearance_Flag")}
                    </StyledWrapper>
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, undefined)(ProductClearance),
    definition: {
        displayName: "ProductClearance",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
